import { strIsNullOrEmpty } from '@fox/atoms';
import { IdentityContext } from '@fox/extensions';
import { ProviderSelection } from '@fox/finance';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Breadcrumbs } from './Breadcrumbs';
import { HamburgerMenu } from './HamburgerMenu';
import { MegaMenu } from './MegaMenu';
import { ActionCentre } from './Membership/action-centre';
import { EventList } from './Membership/event-list';
import { Events } from './Membership/events';
import { initiaLisePaymentMethods } from './Membership/extensions/init-payment-methods';
import { InvoicesTable } from './Membership/financial-details/invoices';
import { PaymentDetails } from './Membership/financial-details/payment-details';
import { ReceiptsTable } from './Membership/financial-details/receipts';
import { SubscriptionsTable } from './Membership/financial-details/subscriptions';
import { GoalsActivitiesTable } from './Membership/goals/activities-table';
import { GoalsSnapShot } from './Membership/goals/goals-snapshot';
import { GoalsTable } from './Membership/goals/goals-table';
import { Importer } from './Membership/importer';
import { LinkLoginRedirect } from './Membership/link-login-redirect';
import { MemberLoginForm } from './Membership/login';
import { MemberLoginStatus } from './Membership/login-status';
import { PasswordResetForm } from './Membership/password-reset';
import { Profile } from './Membership/profiles';
import { RegisterMemberForm } from './Membership/register-member';
import { Representatives } from './Membership/representatives';
import { RequestLoginForm } from './Membership/request-login';
import { RequestPasswordResetForm } from './Membership/request-password-reset';
import { NewsList } from './NewsList';
import { SiteSearch } from './SiteSearch';
import { SwiftFoxForm } from './SwiftFoxForm';
import Video from './Video/Components/Video';
import { SingleAccordion } from './accordion/single-accordion';
import { TabbedAccordions } from './accordion/tabbed-accordions';
import { ComponentClassContextProvider, StyleClasses } from './component-class-context';
import { ComponentStyleContextProvider } from './component-style-context';
import { ContentContextProvider, useContentContext } from './content-context';
import { DynamicEventList } from './dynamic-lists/dynamic-event-list';
import { DynamicNewsList } from './dynamic-lists/dynamic-news-list';
import { EntitySummaryContextProvider } from './entity-summary-context';
import { FinancialContextProvider } from './financial-config-context';
import { GoalsContextProvider } from './goals-context';
import { SwiftFoxTenantConfigProvider } from './swiftfox-tenant-config-context';
import { TableSettingsContextProvider } from './table-settings-context';
import { StylingCustomisations, ThemeProvider } from './theme-provider';

export interface IRootThemedComponentProps {
	requestToken: string;
	contentId: string;
	stylingCustomisations: StylingCustomisations;
	stylingClasses: StyleClasses;
	recaptchaSiteKey?: string;
	paymentProviders?: string[];
	paymentMethods?: string[];
	isLoggedIn?: boolean;
}
/**theme provders need a root theme provider to work properly, if not they will interfere with each other and break styling :( */
export function MountRootThemedComponent(props: IRootThemedComponentProps, el: Element) {
	const root = createRoot(el);
	const element = (
		<ComponentRenderer
			paymentProviders={props.paymentProviders}
			paymentMethods={props.paymentMethods}
			recaptchaSiteKey={props.recaptchaSiteKey}
		/>
	);
	const router = createBrowserRouter([{ path: '*', element }]);
	const queryClient = new QueryClient();
	root.render(
		<ThemeProvider stylingCustomisations={props.stylingCustomisations} stylingClasses={props.stylingClasses}>
			<ContentContextProvider requestToken={props.requestToken} contentId={props.contentId} isLoggedIn={props.isLoggedIn}>
				<IdentityContext.Provider
					value={{
						ensureAuthenticated: async () => true,
						user: { email: '', name: '', sub: '', t: 'hark' },
						authorized: true,
						getAccessToken: () => ''
					}}
				>
					<QueryClientProvider client={queryClient}>
						<RouterProvider router={router} />
					</QueryClientProvider>
				</IdentityContext.Provider>
			</ContentContextProvider>
		</ThemeProvider>
	);
}

//because we share subscription data in a context, we need to place all components using subscriptions in this array
//we do this because we do not want to make any unneeded subscriptions fetches (no org or person subscriptions if we dont need them)
const SUBSCRIPTION_COMPONENTS = ['memberSubscriptions', 'memberPaymentDetails'];
const CUSTOMER_COMPONENTS = ['memberSubscriptions', 'memberPaymentDetails', 'memberInvoices'];
const GOALS_COMPONENTS = ['memberAccreditationSnapshot', 'memberAccreditationGoals', 'memberAccreditationActivity'];
/**Components that rely on the SwiftFox tenant config */
const SWIFTFOX_TENANT_COMPONENTS = ['swiftFoxForm', 'memberReceipts', 'memberProfile', 'memberEventList', 'memberActionCentre'];
/**Components that rely on Recaptcha context */
const RECAPTCHA_COMPONENTS = ['swiftFoxForm'];
/**Components that rely on entity summary */
const ENTITY_SUMMARY_COMPONENTS = ['memberReceipts', 'swiftFoxForm'];

const COMPONENT_MAP = {
	memberLogin: MemberLoginForm,
	memberLoginStatus: MemberLoginStatus,
	memberSubscriptions: SubscriptionsTable,
	memberReceipts: ReceiptsTable,
	memberPasswordReset: PasswordResetForm,
	memberPaymentDetails: PaymentDetails,
	memberProfile: Profile, //logged in member or linked organisation
	memberRegisterForm: RegisterMemberForm,
	memberRequestPasswordResetForm: RequestPasswordResetForm,
	memberEvents: Events,
	newsList: NewsList,
	memberEventList: EventList,
	memberActionCentre: ActionCentre,
	memberRequestLoginForm: RequestLoginForm,
	memberLoginRedirect: LinkLoginRedirect,
	megaMenu: MegaMenu,
	video: Video,
	representatives: Representatives,
	breadcrumbs: Breadcrumbs,
	siteSearch: SiteSearch,
	importer: Importer,
	mobileHeaderLinks: HamburgerMenu,
	memberInvoices: InvoicesTable,
	memberAccreditationSnapshot: GoalsSnapShot,
	memberAccreditationGoals: GoalsTable,
	memberAccreditationActivity: GoalsActivitiesTable,
	accordion: SingleAccordion,
	tabbedAccordions: TabbedAccordions,
	swiftFoxForm: SwiftFoxForm,
	dynamicNewsList: DynamicNewsList,
	dynamicEventsList: DynamicEventList
};

function getTableNames(
	componentsToRender: { elementId: string; componentProps: unknown; component: string; isFinancial?: boolean }[]
): string[] {
	const tableNames = [];
	if (componentsToRender.find(c => c.component === 'memberSubscriptions')) {
		tableNames.push('Subscriptions');
	}
	if (componentsToRender.find(c => c.component === 'memberInvoices')) {
		tableNames.push('Invoices');
	}
	if (componentsToRender.find(c => c.component === 'memberReceipts')) {
		tableNames.push('Receipts');
	}
	return tableNames;
}

function ComponentRenderer({
	paymentMethods,
	paymentProviders,
	recaptchaSiteKey
}: {
	paymentProviders?: string[];
	paymentMethods?: string[];
	recaptchaSiteKey?: string;
}) {
	const { contentId, requestToken } = useContentContext();
	const [initialised, setInitialised] = useState<boolean>(false);
	const [providers, setProviders] = useState<ProviderSelection[]>(undefined);
	const [currencies, setCurrencies] = useState<{
		allowed: string[];
		default: string;
	}>(undefined);
	const [defaultTaxRate, setDefaultTaxRate] = useState<number>(undefined);
	const [taxLabel, setTaxLabel] = useState<string>(undefined);
	const componentsToRender = window.ComponentsToMount ?? [];
	useEffect(() => {
		const init = async () => {
			const res = await initiaLisePaymentMethods(contentId, requestToken, paymentMethods, paymentProviders);
			setProviders(res?.providers);
			setCurrencies(res?.currencies);
			setDefaultTaxRate(res?.defaultTaxRate);
			setTaxLabel(res?.taxLabel);
			setInitialised(true);
		};
		if (componentsToRender?.some(c => c.isFinancial === true)) {
			init();
		} else {
			setInitialised(true);
		}
	}, []);

	if (initialised == false) return null;
	const subscriptionComponents = componentsToRender.filter(c => SUBSCRIPTION_COMPONENTS.includes(c.component));
	const customerComponents = componentsToRender.filter(c => CUSTOMER_COMPONENTS.includes(c.component));
	const goalsComponents = componentsToRender.filter(c => GOALS_COMPONENTS.includes(c.component));
	const swiftFoxTenantConfigComponents = componentsToRender.filter(c => SWIFTFOX_TENANT_COMPONENTS.includes(c.component));
	const recaptchaComponents = componentsToRender.filter(c => RECAPTCHA_COMPONENTS.includes(c.component));
	const entitySummaryComponents = componentsToRender.filter(c => ENTITY_SUMMARY_COMPONENTS.includes(c.component));

	const useLinkedOrganisationSubscriptionData = subscriptionComponents.some(
		c => c.componentProps?.['useLinkedOrganisationData'] === true
	);
	const usePersonSubscriptionData = subscriptionComponents.some(c => c.componentProps?.['useLinkedOrganisationData'] !== true);

	const useLinkedOrganisationCustomerData =
		customerComponents.some(c => c.componentProps?.['useLinkedOrganisationData'] === true) ||
		componentsToRender?.some(c => c.component === 'importer');
	const usePersonCustomerData = customerComponents.some(c => c.componentProps?.['useLinkedOrganisationData'] !== true);
	const useLinkedOrganisationGoalsData = goalsComponents.some(c => c.componentProps?.['useLinkedOrganisationData'] === true);
	const usePersonGoalsData = goalsComponents.some(c => c.componentProps?.['useLinkedOrganisationData'] !== true);
	const tableNames = getTableNames(componentsToRender);

	const usePersonSummary = entitySummaryComponents.some(c => c.componentProps?.['useLinkedOrganisationData'] !== true);
	const useOrganisationSummary = entitySummaryComponents.some(c => c.componentProps?.['useLinkedOrganisationData'] === true);

	const renderComponents = window.ComponentsToMount?.map(c => {
		const Component = COMPONENT_MAP[c.component];
		if (Component) {
			const element = document.getElementById(c.elementId);
			return createPortal(
				//convention: style prop will contain block style and classes prop the classes
				<ComponentStyleContextProvider style={c.componentProps['style']}>
					<ComponentClassContextProvider classes={c.componentProps['classes']}>
						<Component {...(c.componentProps as {})} />
					</ComponentClassContextProvider>
				</ComponentStyleContextProvider>,
				element
			);
		}
	});
	return (
		<TableSettingsContextProvider tableNames={tableNames}>
			<EntitySummaryContextProvider useOrganisationSummary={useOrganisationSummary} usePersonSummary={usePersonSummary}>
				<FinancialContextProvider
					providers={providers ?? []}
					defaultTaxRate={defaultTaxRate}
					taxLabel={taxLabel}
					currencies={currencies}
					useLinkedOrganisationSubscriptionData={useLinkedOrganisationSubscriptionData}
					usePersonSubscriptionData={usePersonSubscriptionData}
					useLinkedOrganisationCustomerData={useLinkedOrganisationCustomerData}
					usePersonCustomerData={usePersonCustomerData}
				>
					<GoalsContextProvider
						useLinkedOrganisationGoalsData={useLinkedOrganisationGoalsData}
						usePersonGoalsData={usePersonGoalsData}
					>
						<SwiftFoxTenantConfigProvider enabled={!!swiftFoxTenantConfigComponents?.length}>
							{!strIsNullOrEmpty(recaptchaSiteKey) && !!recaptchaComponents?.length ? (
								<GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>{renderComponents}</GoogleReCaptchaProvider>
							) : (
								renderComponents
							)}
						</SwiftFoxTenantConfigProvider>
					</GoalsContextProvider>
				</FinancialContextProvider>
			</EntitySummaryContextProvider>
		</TableSettingsContextProvider>
	);
}
